'use client';

import { useState, useEffect, useRef, Suspense, type FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { api, tokenStore, ApiClientError } from '@/lib/api-client';
import Link from 'next/link';
// D-LOW-003 fix (SPEC-044 QA AUDIT R1 — Batch 4): explicit `<Label htmlFor>` +
// `<Input id>` replaces implicit `<label><span>X</span><input/></label>`.
// See forgot-password/page.tsx for the full accessibility rationale
// (WCAG 2.1 SC 1.3.1 + SC 4.1.2).
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
// SPEC-050 Bug 1: replace native `<button>` with shadcn `<Button>` so the
// auth action inherits theme-aware tokens (`bg-primary`, `text-primary-foreground`,
// `focus-visible:ring-ring`) + the Bug 8 mobile touch-target sizing.
import { Button } from '@/components/ui/button';
// SPEC-081 Fase 2 §6.12: Store icon for the "Vender en Profileia Market"
// CTA button below the login form. Links to /seller/onboarding if the
// `market_sellers_enabled` feature flag is on. The flag is fetched on
// mount (best-effort — defaults to `false` if the fetch fails, so the
// button is hidden until the flag is confirmed `true`). The backend
// independently rejects requests to /api/seller/* with 403
// `MARKET_SELLERS_DISABLED` when the flag is off (defense in depth —
// the frontend gate is a UX affordance, not the authoritative check).
// P-198 (SPEC-131): ChevronLeft icon for the "Volver al inicio" link
// displayed above the login form card. Mirrors the pattern already used
// on /plan-de-compensacion (frontend/src/app/plan-de-compensacion/page.tsx).
import { ChevronLeft, Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * Bug #34 fix (R3 CORROBORACIÓN QA): sanitize the ?next= redirect parameter to
 * prevent OPEN REDIRECT attacks. An attacker could craft a phishing URL like:
 *   /login?next=https://evil.com
 * After successful login, the user would be redirected to evil.com — leaking
 * the access token in the Authorization header if the user is tricked into
 * clicking anything that triggers an API call.
 *
 * Fix: ONLY allow relative paths (starting with '/') that are NOT protocol-
 * relative ('//evil.com'). External URLs (http://, https://, //) are rejected
 * and the user is redirected to the default '/app' route.
 *
 * Spec ref: SPEC-NEGOCIO §9.6 (INV-SEC-06) + OWASP Open Redirect best practice.
 */
function sanitizeNext(next: string | null): string {
  if (!next) return '/app';
  // Must start with '/' but NOT '//'
  if (!next.startsWith('/') || next.startsWith('//')) {
    return '/app';
  }
  // Reject backslashes (could be used to bypass: /\\evil.com)
  if (next.startsWith('/\\')) {
    return '/app';
  }
  // Reject anything that looks like a URL scheme: /javascript:, /data:, etc.
  if (/^[a-z][a-z0-9+.-]*:/i.test(next)) {
    return '/app';
  }
  return next;
}

/**
 * Founder decision 2026-08-29: /login ALWAYS goes to /app (user dashboard)
 * regardless of role. Superadmins access the admin panel via /admin/login
 * (separate URL). This eliminates the AUDIT-070-001 auto-redirect that
 * forced superadmins to /admin after every login from /login.
 *
 * The resolvePostLoginTarget function is kept as a no-op passthrough for
 * backward compatibility with existing call sites — it simply returns `next`
 * without any role-based redirect.
 */
function resolvePostLoginTarget(
  next: string,
   
  _userRole: string | undefined,
): string {
  return next;
}

/**
 * Login page — wraps the form in a Suspense boundary because useSearchParams()
 * requires it during static prerender (Next.js 16 requirement).
 *
 * Spec ref: SPEC-003-FRONTEND-BACKEND-INTEGRATION §3,
 *           SPEC-010 §3.1.3 (2FA login flow)
 */
export default function LoginPage() {
  return (
    <Suspense fallback={<LoginFallback />}>
      <LoginContent />
    </Suspense>
  );
}

function LoginFallback() {
  return (
    <main id="main-content" className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="text-muted-foreground">Cargando…</div>
    </main>
  );
}

function LoginContent() {
  const router = useRouter();
  const params = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // SPEC-178 P1-2 (Sprint 91 QA): password visibility toggle. Default: hidden.
  // Toggling shows the password in plain text so the user can verify what
  // they typed before submitting — common pattern in modern logins (Google,
  // GitHub, Vercel). The toggle button is inside the password input wrapper
  // (absolute positioned) so it doesn't break the form layout.
  const [showPassword, setShowPassword] = useState(false);
  // SPEC-178 P2-1 (Sprint 91 QA): "Recordar mi email" checkbox. When checked,
  // the email is persisted in localStorage so the next visit pre-fills the
  // email field. This is a UX affordance — the actual auth still happens
  // server-side. The autocomplete="email" attribute already helps browsers
  // remember, but an explicit checkbox gives the user control + clarity.
  const [rememberEmail, setRememberEmail] = useState(false);

  // Load remembered email on mount (best-effort).
  useEffect(() => {
    try {
      const saved = localStorage.getItem('profileia:remembered-email');
      if (saved) {
        setEmail(saved);
        setRememberEmail(true);
      }
    } catch {
      // localStorage unavailable (private mode) — non-blocking.
    }
  }, []);

  // SPEC-056 ÁREA C AUDIT-V25-008 (2026-08-22): rate-limit countdown.
  // When the backend returns 429 with code='RATE_LIMIT_EXCEEDED', we show
  // a live countdown ("Intenta de nuevo en 47s") instead of the static
  // "Demasiados intentos. Intenta de nuevo en un minuto." message.
  // The backend's `Retry-After` header (60s for auth) isn't exposed via
  // `ApiClientError` (only `message`, `code`, `status`, `details`), so we
  // use a fixed 60s countdown — matches the backend's auth rate limit
  // window (60s, 5 attempts — backend/src/middleware/rate-limit.middleware.ts:122).
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (rateLimitCountdown <= 0) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }
    countdownIntervalRef.current = setInterval(() => {
      setRateLimitCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [rateLimitCountdown]);

  // SPEC-010 §3.1.3 — 2FA flow state
  const [requires2FA, setRequires2FA] = useState(false);
  // tempToken is set but only used via tokenStore.setAccess() — the state
  // variable itself is kept for clarity (tracks that we're in 2FA flow with
  // a temp token) but eslint flags it as unused. Prefix with _ to acknowledge.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');

  // SPEC-178 P0-1 (Sprint 91 QA): the "Vender en Profileia Market" CTA was
  // removed from /login because /seller/onboarding requires auth — anonymous
  // visitors clicking the CTA were redirected back to /login?reason=session_expired,
  // creating a frustrating loop. A public /seller landing page (SPEC-178 follow-up)
  // will replace this CTA once created. The marketSellersEnabled flag fetch was
  // removed along with the CTA (no longer needed).

  useEffect(() => {
    // KNOWN: Token store check is one-time on mount. Cross-tab token changes won't trigger redirect.
    // Si ya hay sesión, ir al dashboard (SPEC-001 §2: ruta /app)
    // o a la ruta original que el usuario intentaba acceder (?next=)
    if (tokenStore.getAccess()) {
      // Bug #34 fix: sanitize ?next= to prevent open redirect
      const next = sanitizeNext(params.get('next'));
      router.replace(next as Parameters<typeof router.replace>[0]);
    }
    const reason = params.get('reason');
    // AUDIT-063-008 fix (2026-08-23): extend the reason handlers so the
    // page surfaces an inline error banner for ALL auth-failure reasons
    // the backend/middleware can dispatch — previously only `expired`
    // and `unauthenticated` were handled, leaving `invalid_credentials`
    // and `2fa_required` silently swallowed (no visible feedback). The
    // audit reports users seeing the form with no indication of what
    // went wrong. `unauthenticated` and `expired` keep their existing
    // copy (more accurate than the audit's literal suggestion) — the
    // audit's wording for `invalid_credentials` + `2fa_required` is
    // applied as-is.
    // Spec ref: docs/SPEC-063-QA-AUDIT-2026-08-23.md §AUDIT-063-008
    if (reason === 'expired' || reason === 'session_expired') {
      setError('Tu sesión expiró. Inicia sesión de nuevo.');
    } else if (reason === 'unauthenticated') {
      setError('Debes iniciar sesión para acceder a esa página.');
    } else if (reason === 'invalid_credentials') {
      setError('Email o contraseña incorrectos.');
    } else if (reason === '2fa_required') {
      setError('Necesitas verificar 2FA para acceder.');
    } else if (reason === 'password-changed') {
      setError('Tu contraseña fue cambiada. Inicia sesión de nuevo.');
    }
  }, [router, params]);

  // H-05 fix: If the user is in the 2FA step and navigates away (closes tab,
  // uses browser back, etc.) without clicking "Volver al login", the tempToken
  // persists in tokenStore. This cleanup clears it on unmount or beforeunload.
  useEffect(() => {
    if (!requires2FA) return;
    const handleUnload = () => { tokenStore.clear(); };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      // Also clear on component unmount while in 2FA mode
      if (requires2FA) tokenStore.clear();
    };
  }, [requires2FA]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // SPEC-178 P2-1 (Sprint 91 QA): persist email if "Recordar" is checked.
      // Cleared if unchecked. Wrapped in try/catch for private mode / quota.
      try {
        if (rememberEmail) {
          localStorage.setItem('profileia:remembered-email', email);
        } else {
          localStorage.removeItem('profileia:remembered-email');
        }
      } catch {
        // Non-blocking — localStorage unavailable.
      }

      const res = await api.login({ email, password });
      // SPEC-010 §3.1.3 — Si login retorna requires2FA, mostrar input de código
      if ('requires2FA' in res && res.requires2FA) {
        setRequires2FA(true);
        setTempToken(res.tempToken);
        // Guardar tempToken como access token para usarlo en verify-2fa
        tokenStore.setAccess(res.tempToken);
        setLoading(false);
        return;
      }
      // Login normal — tokens ya guardados en api.login()
      // Bug #34 fix: sanitize ?next= to prevent open redirect
      const next = sanitizeNext(params.get('next'));
      // AUDIT-070-001 (SPEC-070 QA Audit staging 2026-08-25 v6):
      // For superadmin without an explicit ?next=, redirect to /admin
      // instead of /app (the default user dashboard). The backend's
      // `POST /auth/login` returns the user object via `toPublic()` when
      // 2FA is NOT required — the `AuthResponse.user.role` field is the
      // gate. When 2FA IS required, the response shape is `Login2FAResult`
      // (no user object yet) and the role-based redirect happens in
      // `onVerify2FA` after the 2FA code is verified.
      //
      // We use `'user' in res` as a TypeScript narrowing guard: TS does
      // not propagate the `Login2FAResponse` narrowing through the
      // `&& res.requires2FA` short-circuit in the early-return above,
      // so the union is still `AuthResponse | Login2FAResponse` here.
      // The `'user' in res` check is both a runtime safety guard (defense
      // in depth — if the backend changes the response shape) AND a TS
      // narrowing hint (inside the truthy branch `res` is `AuthResponse`).
      // §6 Anti-Invención: NO business logic — only UX redirect.
      // Trazabilidad §6: SPEC-070 §AUDIT-070-001, SPEC-NEGOCIO §8.1.
      const userRole = 'user' in res ? res.user?.role : undefined;
      const targetPath = resolvePostLoginTarget(next, userRole);
      // AUDIT-063-002 fix: use `window.location.href` instead of
      // `router.replace()` to avoid the same cookie-timing race
      // condition fixed for the admin login in SPEC-061-v2 (PR #92).
      // The api.login() call sets the `profileia.refresh` httpOnly
      // cookie via the `Set-Cookie` response header. `router.replace`
      // fires a Next.js RSC navigation which can land on /app/* before
      // the browser has finished storing the cookie → the middleware
      // (frontend/src/middleware.ts) sees no cookie → redirects to
      // /login?reason=unauthenticated. `window.location.href` does a
      // FULL page reload, which guarantees the cookie is sent with
      // the next request.
      //
      // Spec ref: docs/SPEC-063-QA-AUDIT-2026-08-23.md §AUDIT-063-002
      // Reference PR: #92 (SPEC-061-v2 admin login race condition fix).
      // Fix 2026-08-29: use replace() for faster redirect.
      // PR #1586 (CRITICAL fix): clear `sessionExpired` flag before
      // navigating. `window.location.replace` does a full page reload,
      // which resets `window.__profileia` to undefined → flag cleared
      // automatically. But if for any reason navigation is intercepted
      // (e.g., browser extension, beforeunload handler) we want the flag
      // cleared so a subsequent AppShell mount doesn't trigger the loop.
      if (typeof window !== 'undefined' && window.__profileia) {
        window.__profileia.sessionExpired = false;
      }
      window.location.replace(targetPath);
      return;
    } catch (err) {
      // AUDIT-017 fix (FASE 3 C3): el catch anterior usaba `err.message`
      // directamente, pero si `err` no es una instancia de Error estándar
      // (ej: un objeto response JSON sin prototype), `err.message` puede ser
      // undefined y el setError recibía el objeto entero → se mostraba como
      // "[object Object]". Ahora extraemos el mensaje de forma segura.
      if (err instanceof ApiClientError) {
        // SPEC-056 ÁREA C AUDIT-V25-008: si el backend devuelve 429 con
        // code='RATE_LIMIT_EXCEEDED', disparamos un countdown de 60s en
        // lugar de mostrar el mensaje estático "Demasiados intentos...".
        if (err.status === 429 && err.code === 'RATE_LIMIT_EXCEEDED') {
          setRateLimitCountdown(60);
          setError(err.message || 'Demasiados intentos. Intenta de nuevo en un minuto.');
        } else {
          setError(err.message || 'Error al iniciar sesión.');
        }
      } else if (err instanceof Error) {
        setError(err.message || 'Error al iniciar sesión.');
      } else if (typeof err === 'string') {
        setError(err);
      } else if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
        setError((err as { message: string }).message);
      } else {
        setError('Error inesperado. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function onVerify2FA(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await api.verify2FA(twoFACode);
      if (result.verified) {
        // F-25: verify2FA now returns fresh tokens (access + refresh)
        // Store the new access token — refresh cookie is set by backend
        if (result.tokens?.accessToken) {
          tokenStore.setAccess(result.tokens.accessToken);
        }
        // AUDIT-1-BUG-007: set `requires2FA(false)` BEFORE redirect.
        setRequires2FA(false);
        const next = sanitizeNext(params.get('next'));
        const targetPath = resolvePostLoginTarget(next, result.user?.role);
        // Use replace() instead of href= for faster redirect (doesn't
        // add a history entry + triggers navigation immediately).
        // Fix 2026-08-29: previous code used window.location.href which
        // was slow because the browser waited for the current page's
        // JS to finish before navigating.
        // PR #1586 (CRITICAL fix): clear `sessionExpired` flag before
        // navigating (same rationale as the non-2FA login path above).
        if (typeof window !== 'undefined' && window.__profileia) {
          window.__profileia.sessionExpired = false;
        }
        window.location.replace(targetPath);
        // Return early — the finally block will set loading=false but
        // the page is already navigating away. This prevents the user
        // from seeing "Verificando..." for 5-10s while the redirect
        // completes.
        return;
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message || 'Código 2FA inválido. Intenta de nuevo.');
      } else if (err instanceof Error) {
        setError(err.message || 'Código 2FA inválido. Intenta de nuevo.');
      } else if (typeof err === 'string') {
        setError(err);
      } else {
        setError('Código 2FA inválido. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  }

  // SPEC-050 Bug 1: Brand gradient CTA shared className. The gradient uses
  // brand colors (#2563eb → #8b5cf6) and works in both light and dark mode
  // (cyan-to-violet over `bg-background` reads well in either). `text-white`
  // stays legible on the gradient in both themes.
  const brandGradientCta =
    'w-full bg-gradient-to-r from-[#2563eb] to-[#8b5cf6] text-white hover:opacity-90 shadow-lg';

  // SPEC-178 P2-6 (Sprint 91 QA): branded background gradient. Uses brand
  // colors (#2563EB → #8B5CF6) at low opacity over bg-background so the form
  // card stands out. Works in both light + dark mode (gradient is subtle).
  // Mirrors the hero gradient from the landing page (page.tsx line 346).
  // SPEC-178 P2-4 (Sprint 91 QA): desktop layout uses 2 columns — form on
  // left (max-w-md), benefits panel on right (hidden on mobile, shown on lg+).
  // This avoids the "half-empty" look on desktop without complicating mobile.
  return (
    <main id="main-content" className="min-h-screen flex items-center justify-center px-4 py-12 bg-background relative overflow-hidden">
      {/* Background gradient orbs (brand colors, low opacity) */}
      <div className="absolute top-20 -left-32 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 bg-[#8B5CF6]/10 rounded-full blur-3xl" aria-hidden="true" />

      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center relative z-10">
      {/* LEFT COLUMN: form (col-span-1 on desktop, full width on mobile) */}
      <div className="w-full max-w-md mx-auto">
 {/* P-198 (): link "Volver al inicio" — visible above the
            branding + form card so the user can return to the public landing
            without using the browser back button. Sits OUTSIDE the form card
            so it doesn't compete with the primary "Entrar" CTA. */}
        <div className="mb-6 text-center">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ChevronLeft className="size-4" />
            Volver al inicio
          </Link>
        </div>

 {/* P1-5 (Sprint 91 QA): H1 = "Iniciar sesión" (was "Profileia").
            The page H1 should describe the page's purpose, not repeat the brand.
            SEO + a11y best practice: H1 unique per page. The brand "Profileia"
            moves to H2 (visually still prominent via the colored spans). */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Iniciar sesión
          </h1>
          <h2 className="text-4xl font-bold tracking-tight mt-2">
            <span className="text-[#06b6d4]">Profile</span>
            <span className="text-[#8b5cf6]">ia</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Tu perfil inteligente. La casa del emprendedor.
          </p>
        </div>

        {!requires2FA ? (
          // ─── Login normal ────────────────────────────────────────────────────
          <form
            onSubmit={onSubmit}
            className="bg-card border border-border backdrop-blur rounded-2xl p-8 shadow-2xl"
          >

            {error && (
              <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" role="alert">
                {error}
 {/* ÁREA C AUDIT-V25-008: live countdown when rate-limited.
                    The error message stays the same ("Demasiados intentos...")
                    but the user sees the seconds ticking down so they know
                    exactly when they can retry — instead of guessing "un minuto". */}
                {rateLimitCountdown > 0 && (
                  <p className="mt-1 font-mono text-xs">
                    Intenta de nuevo en {rateLimitCountdown}s…
                  </p>
                )}
              </div>
            )}

 {/* P1-3 (Sprint 91 QA): added placeholder for email input.
                Placeholder gives the user a hint about the expected format
                (tu@email.com) without competing with the visible label. */}
            <div className="block mb-4">
              <Label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1">Email</Label>
              <Input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </div>

 {/* P1-3 + P1-2 (Sprint 91 QA): placeholder + password toggle.
                The toggle button is absolute-positioned inside the input wrapper
                so it overlays the right side of the input. `type` switches between
                'password' (masked) and 'text' (visible) based on showPassword state. */}
            <div className="block mb-6">
              <Label htmlFor="login-password" className="block text-sm font-medium text-foreground mb-1">Contraseña</Label>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="size-4" aria-hidden="true" />
                  ) : (
                    <Eye className="size-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

 {/* P2-1 (Sprint 91 QA): "Recordar mi email" checkbox.
                Persisted in localStorage on submit. Pre-fills email on next visit. */}
            <div className="flex items-center gap-2 mb-4 -mt-2">
              <input
                type="checkbox"
                id="remember-email"
                checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
                className="size-4 rounded border-border accent-[#2563EB]"
              />
              <Label htmlFor="remember-email" className="text-sm text-muted-foreground cursor-pointer">
                Recordar mi email
              </Label>
            </div>

 {/* P2-5 (Sprint 91 QA): spinner Loader2 in button during
                submit. Previously just changed text to "Entrando…" — now also
                shows a spinning icon for clearer visual feedback. */}
            <Button type="submit" disabled={loading} className={brandGradientCta}>
              {loading ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" />
                  Entrando…
                </>
              ) : (
                'Entrar'
              )}
            </Button>

            <div className="mt-4 text-center">
              <Link href="/forgot-password" className="text-sm text-primary hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              ¿No tienes cuenta?{' '}
              <Link href="/register" className="text-primary hover:underline">
                Crear cuenta
              </Link>
            </p>

 {/* P0-1 (Sprint 91 QA): "Vender en Profileia Market" CTA REMOVED.
                The CTA linked to /seller/onboarding which requires auth — anonymous
                visitors clicking it were redirected back to /login?reason=session_expired,
 creating a frustrating loop. A public /seller landing page (
                follow-up) will replace this CTA once created.
                Previous code (lines 443-468) had: {marketSellersEnabled && (<Link href="/seller/onboarding">...)}
                The marketSellersEnabled state + useEffect fetch were also removed (no
                longer needed without the CTA). */}

 {/* MTRX-013 (): admin login link REMOVED per Founder
                feedback 2026-08-26 (AUDIT-074-001):
                "los usuarios no deben saber por donde accede el Admin".
                El admin accede directamente via /admin/login (URL no
                linkeada desde la UI pública). */}
          </form>
        ) : (
          // ─── 2FA verification ──────────────────────────────────────────────
          <form
            onSubmit={onVerify2FA}
            className="bg-card border border-border backdrop-blur rounded-2xl p-8 shadow-2xl"
          >
            <h2 className="text-xl font-semibold mb-2 text-foreground">Verificación en 2 pasos</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Ingresa el código de tu aplicación de autenticación (Google Authenticator, Authy, etc.)
            </p>

            {error && (
              <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="block mb-6">
              {/* AUDIT-065-001: Accept both 6-digit TOTP and 8-char backup codes.
                  Backup codes are 8 lowercase hex chars (0-9, a-f) — see
                  auth.service.ts:767 `randomBytes(4).toString('hex')`. */}
              <Label htmlFor="login-2fa-code" className="block text-sm font-medium text-foreground mb-1">Código de 6 dígitos o código de respaldo de 8</Label>
              <Input
                id="login-2fa-code"
                type="text"
                required
                inputMode="text"
                pattern="\d{6}|[0-9a-f]{8}"
                maxLength={8}
                autoComplete="one-time-code"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.toLowerCase().replace(/[^0-9a-f]/g, '').slice(0, 8))}
                className="text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
                autoFocus
              />
            </div>

            {/* AUDIT-065-001: Submit enabled for 6 (TOTP) OR 8 (backup) digits */}
            <Button type="submit" disabled={loading || (twoFACode.length !== 6 && twoFACode.length !== 8)} className={brandGradientCta}>
              {loading ? 'Verificando…' : 'Verificar'}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRequires2FA(false);
                setTempToken(null);
                setTwoFACode('');
                setError(null);
                setLoading(false); // L-07 fix: ensure loading is reset when going back
                tokenStore.clear();
              }}
              className="w-full mt-3"
            >
              Volver al login
            </Button>
          </form>
        )}
      </div>

      {/* RIGHT COLUMN: benefits panel (hidden on mobile, shown on lg+).
 P2-4 (Sprint 91 QA): fills the empty right side on desktop
          with a benefits list + testimonial-style quote. Mirrors the pattern
          used by Vercel, Linear, Notion login pages. */}
      <div className="hidden lg:flex flex-col justify-center px-8 py-12">
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-6">
          Tu casa del emprendedor
        </h2>
        <p className="text-muted-foreground mb-8">
          Reunimos tu academia, tus herramientas, tu marketplace y tu compensación
          en un solo perfil inteligente.
        </p>
        <ul className="space-y-4">
          <li className="flex items-start gap-3">
            <span className="size-6 rounded-full bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
            <div>
              <p className="font-medium text-foreground">7 módulos integrados</p>
              <p className="text-sm text-muted-foreground">Tarjeta Digital, Conference, Landing de Captura, Academia, Market, Matriz Viva y Profileia Hub.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="size-6 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
            <div>
              <p className="font-medium text-foreground">0% comisión en depósitos</p>
              <p className="text-sm text-muted-foreground">Tu saldo llega completo a tu cuenta Profileia.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="size-6 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
            <div>
              <p className="font-medium text-foreground">Plan de compensación transparente</p>
              <p className="text-sm text-muted-foreground">Bono directo $10 + matriz viva + viaje nacional Diamante.</p>
            </div>
          </li>
        </ul>
        <blockquote className="mt-8 pl-4 border-l-2 border-[#2563EB]/30 text-sm text-muted-foreground italic">
          “Antes pagaba 5 herramientas que no se hablaban entre sí. Hoy todo vive en mi enlace.”
          <footer className="mt-1 not-italic text-xs">— Testimonio pronto</footer>
        </blockquote>
      </div>
      </div>

 {/* P3-3 (Sprint 91 QA): JSON-LD WebPage structured data.
          Although /login is noindex,nofollow, adding WebPage + BreadcrumbList
          is consistent with the rest of the site + helps crawlers that ignore
          noindex (rare but exists) understand the page hierarchy. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Iniciar sesión — Profileia',
            description: 'Accede a tu cuenta Profileia para gestionar tu Tarjeta Digital, Conference y comisiones.',
            url: 'https://profileia.com/login',
            isPartOf: {
              '@type': 'WebSite',
              name: 'Profileia',
              url: 'https://profileia.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://profileia.com/' },
                { '@type': 'ListItem', position: 2, name: 'Iniciar sesión', item: 'https://profileia.com/login' },
              ],
            },
          }),
        }}
      />
    </main>
  );
}
